import { Box, Popover, Typography } from "@mui/material";
import { ControlledTextFormField } from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/controlled-fields/controlled-text-field";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React, { FC, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { ExtendedFieldError } from "vmd-types";
/**
 * Defines a status entry with name and color.
 */
export interface StatusEntry {
  name: string;
  color?: string;
  label: string;
  placeholder: string;
}

export interface StatusFieldsProps {
  /**
   * Initial list of status entries.
   */
  initialEntries?: StatusEntry[];
  /**
   * Called whenever the entries change (add, remove, name or color update).
   */
  onChange?: (entries: StatusEntry[]) => void;

  enableColorPicker?: boolean;
  title?: string;
  validationErrors: Record<string, ExtendedFieldError>;
  type: string;
  language?: string;
}

export const StatusFields: FC<StatusFieldsProps> = ({
  initialEntries = [],
  enableColorPicker,
  onChange,
  title = "Define which degree of completion the tracking item can assume.",
  validationErrors,
  type,
  language = "en",
}) => {
  const [entries, setEntries] = useState<StatusEntry[]>(initialEntries);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const translateEntryLabel = (label: string): string => {
    switch (label) {
      case "Choose a label for progress not yet started":
        return (
          getLocalizedMessage(language, "statusFields.progressNotStarted") ||
          "Choose a label for progress not yet started"
        );
      case "Choose a label for in progress":
        return (
          getLocalizedMessage(language, "statusFields.progressInProgress") ||
          "Choose a label for in progress"
        );
      case "Choose a label for completed":
        return (
          getLocalizedMessage(language, "statusFields.progressCompleted") ||
          "Choose a label for completed"
        );
      case "Choose a label for On‑Track items":
        return (
          getLocalizedMessage(language, "statusFields.onTrackItems") ||
          "Choose a label for On‑Track items"
        );
      case "Choose a label for At‑Risk items":
        return (
          getLocalizedMessage(language, "statusFields.atRiskItems") ||
          "Choose a label for At‑Risk items"
        );
      case "Choose a label for Critical items":
        return (
          getLocalizedMessage(language, "statusFields.criticalItems") ||
          "Choose a label for Critical items"
        );
      case "Choose a label for very low severity":
        return (
          getLocalizedMessage(language, "statusFields.veryLowSeverity") ||
          "Choose a label for very low severity"
        );
      case "Choose a label for low severity":
        return (
          getLocalizedMessage(language, "statusFields.lowSeverity") ||
          "Choose a label for low severity"
        );
      case "Choose a label for medium severity":
        return (
          getLocalizedMessage(language, "statusFields.mediumSeverity") ||
          "Choose a label for medium severity"
        );
      case "Choose a label for high severity":
        return (
          getLocalizedMessage(language, "statusFields.highSeverity") ||
          "Choose a label for high severity"
        );
      case "Choose a label for critical severity":
        return (
          getLocalizedMessage(language, "statusFields.criticalSeverity") ||
          "Choose a label for critical severity"
        );
      default:
        return label;
    }
  };

  const translateEntryPlaceholder = (placeholder: string): string => {
    switch (placeholder) {
      case "Not Started":
        return (
          getLocalizedMessage(language, "statusFields.notStarted") ||
          "Not Started"
        );
      case "In Progress":
        return (
          getLocalizedMessage(language, "statusFields.inProgress") ||
          "In Progress"
        );
      case "Done":
        return getLocalizedMessage(language, "statusFields.done") || "Done";
      case "On Track":
        return (
          getLocalizedMessage(language, "statusFields.onTrack") || "On Track"
        );
      case "At Risk":
        return (
          getLocalizedMessage(language, "statusFields.atRisk") || "At Risk"
        );
      case "Critical":
        return (
          getLocalizedMessage(language, "statusFields.critical") || "Critical"
        );
      case "Very Low":
        return (
          getLocalizedMessage(language, "statusFields.veryLow") || "Very Low"
        );
      case "Low":
        return getLocalizedMessage(language, "statusFields.low") || "Low";
      case "Medium":
        return getLocalizedMessage(language, "statusFields.medium") || "Medium";
      case "High":
        return getLocalizedMessage(language, "statusFields.high") || "High";
      default:
        return placeholder;
    }
  };

  const notifyChange = (newEntries: StatusEntry[]) => {
    setEntries(newEntries);
    onChange?.(newEntries);
  };

  const handleNameChange = (index: number, value: string) => {
    const updated = [...entries];
    updated[index].name = value;
    notifyChange(updated);
  };

  const handleColorChange = (index: number, color: string) => {
    const updated = [...entries];
    updated[index].color = color;
    notifyChange(updated);
  };

  const togglePicker = (
    event: React.MouseEvent<HTMLElement>,
    index: number
  ) => {
    if (!enableColorPicker) return;
    if (openIndex === index) {
      setOpenIndex(null);
      setAnchorEl(null);
    } else {
      setOpenIndex(index);
      setAnchorEl(event.currentTarget);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 10,
        width: "100%",
        border: "1px solid #E0E0E0",
        padding: 2,
        borderRadius: "12px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography sx={{ fontWeight: 400, minWidth: 280, fontSize: 14 }}>
          {title}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 14,
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 500, minWidth: 280, fontSize: 14 }}>
            {getLocalizedMessage(language, "name") || "Name"}
          </Typography>
          <Typography sx={{ fontWeight: 500, fontSize: 14 }}>
            {getLocalizedMessage(language, "color") || "Color"}
          </Typography>
        </Box>
        {entries.map((entry, index) => (
          <Box
            key={entry.label}
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 5,
              alignItems: "center",
            }}
          >
            <Box sx={{ width: 360, height: "100%" }}>
              <Typography sx={{ fontWeight: 500, minWidth: 280, fontSize: 14 }}>
                {translateEntryLabel(entry.label)}
              </Typography>
              <ControlledTextFormField
                placeholder={translateEntryPlaceholder(entry.placeholder)}
                value={entry.name}
                onChange={(val) => handleNameChange(index, val)}
                error={Boolean(validationErrors?.[`${type}.${index}.name`])}
                helperText={
                  validationErrors?.[`${type}.${index}.name`]?.message
                }
              />
            </Box>
            <Box>
              <Box
                onClick={(e) => togglePicker(e, index)}
                sx={{
                  height: 30,
                  width: 30,
                  background: entry.color,
                  "&:hover": {
                    background: `${entry.color}50`,
                  },
                  borderRadius: "50%",
                }}
              />
              <Popover
                open={openIndex === index}
                anchorEl={anchorEl}
                onClose={() => setOpenIndex(null)}
                sx={{ zIndex: 2000, borderRadius: "12px" }}
              >
                <HexColorPicker
                  color={entry.color}
                  onChange={(color) => handleColorChange(index, color)}
                />
              </Popover>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
